/**
 * Chance no topo (§8 de 14/10, noite, C01 e K04; proposta do front de 14/10, seção 3): a faixa compacta da chance
 * dentro do cartão principal, visível também no celular, no tablet e em telas baixas, onde o `ProbabilitySlot` não
 * cabe. Mostra a chance dos dois, na ordem do par (13 | 22), o selo "Projeção" e, quando o motor manda `final_votes`
 * (P-35), a faixa de votos final de cada um. Só com a chance ativa, pelas mesmas regras do slot (`stripActive`).
 */

import type { ProbabilityPayload, VoteQuantiles } from '../../contracts/probability';
import type { Candidate } from '../../contracts/static';
import { t } from '../../copy/strings';
import { fmtChancePairShown, fmtQtyShort, fmtVotesLong } from '../../format/numbers';
import { chanceCalled } from '../../model/probability';
import { partyClass, partyOf } from '../../theme/parties';
import './ProbabilityStrip.css';

export interface ProbabilityStripProps {
  /** Chance de cada número do par. */
  win: Readonly<Record<string, number>>;
  /** P-35: faixa de votos final de cada um; null ou ausente = sem a linha de votos. */
  votes?: Readonly<Record<string, VoteQuantiles>> | null;
  /** Chamada "mais de 99" aprovada (só presidente; P-05: governador nunca). */
  called?: boolean;
  candidates: readonly [
    Pick<Candidate, 'number' | 'display_name' | 'party_code'>,
    Pick<Candidate, 'number' | 'display_name' | 'party_code'>,
  ];
}

/** As props da faixa a partir do resumo da chance do presidente (`probability` do snapshot). */
export function presidentStrip(p: ProbabilityPayload): Pick<ProbabilityStripProps, 'win' | 'votes' | 'called'> {
  return {
    win: p.win_prob ?? {},
    votes: p.final_votes ?? null,
    called: chanceCalled(p),
  };
}

export function ProbabilityStrip({ win, votes = null, called = false, candidates }: ProbabilityStripProps) {
  const [a, b] = candidates;
  // P-30 item 1: "mais de 99" só com a chamada aprovada (`call`), nunca pelo número; sem ela, no máximo 99 e 1
  const chances = fmtChancePairShown(win[a.number] ?? Number.NaN, win[b.number] ?? Number.NaN, called);
  const values = chances.map((c) => t('prob.chance.value', { C: c })) as [string, string];
  const va = votes?.[a.number];
  const vb = votes?.[b.number];
  const range = (q: { q10: number; q90: number }) =>
    t('prob.final.range', { lo: fmtQtyShort(q.q10), hi: fmtVotesLong(q.q90) });
  return (
    <div class="probability-strip" data-testid="probability-strip">
      <p class="sr-only">
        {t('prob.chance.sr', { A: a.display_name, pA: values[0], B: b.display_name, pB: values[1] })}
      </p>
      <p class="probability-strip__line" aria-hidden="true">
        <span class="probability-strip__label">{t('prob.chance.label')}</span>
        {candidates.map((c, i) => (
          <span key={c.number} class={`probability-strip__side ${partyClass(partyOf(c))}`} data-number={c.number}>
            <i class="probability-strip__swatch" data-dataviz="" />
            <span class="probability-strip__name">{c.display_name}</span>{' '}
            <b class="probability-strip__value">{values[i]}</b>
          </span>
        ))}
        <span class="probability-strip__badge">{t('prob.title')}</span>
      </p>
      {va && vb ? (
        <p class="probability-strip__votes" data-testid="probability-strip-votes">
          {t('prob.strip.votes', { A: a.display_name, rA: range(va), B: b.display_name, rB: range(vb) })}
        </p>
      ) : null}
    </div>
  );
}
